/**
 * Snapshot API Client
 * Functions for interacting with the Five-Swing Snapshot system
 */

import { edgeInvoke, edgeGet } from '@/lib/edgeInvoke';
import { supabase } from '@/integrations/supabase/client';
import { getThumbnailUrl } from '@/lib/video/cloudflare-playback-resolver';

const getCloudflareUidFromPlaybackUrl = (playbackUrl?: string | null) => {
  if (!playbackUrl) return null;
  const match = playbackUrl.match(/\/([a-f0-9]{32})\//i);
  return match ? match[1] : null;
};

export interface SnapshotRequest {
  swing_ids: string[];
}

export interface SnapshotResult {
  snapshotId: string;
  primaryFault: {
    taxonomy_key: string;
    display_name?: string;
    avg_severity: number;
    consistency: number;
  };
  strengths: Array<{
    strength_key: string;
    occurrences: number;
  }>;
  swing_quality: number;
  peer_label: string;
}

export interface SnapshotResponse {
  snapshot: {
    id: string;
    user_id: string;
    swing_ids: string[];
    primary_fault: string | null;
    strengths: any;
    fault_summary: any;
    swing_quality: number;
    peer_label: string;
    created_at: string;
    updated_at: string;
  };
  faults: Array<{
    id: string;
    snapshot_id: string;
    taxonomy_key: string;
    avg_severity: number;
    avg_certainty: number;
    consistency: number;
  }>;
  strengths: Array<{
    id: string;
    snapshot_id: string;
    strength_key: string;
    occurrences: number;
  }>;
  swing_runs: Array<{
    id: string;
    created_at: string;
    thumbnail_url: string | null;
    filename: string | null;
    swing_quality: number;
  }>;
}

/**
 * @deprecated PLA-138: Use createSnapshotV2 instead.
 * This function calls the legacy 'snapshot-aggregate' endpoint.
 * The new 'snapshot-run-v2' endpoint provides proper provenance tracking.
 */
export async function createSnapshot(_swingIds: string[]): Promise<SnapshotResult> {
  throw new Error(
    'createSnapshot is deprecated. Use createSnapshotV2 instead (PLA-138). ' +
    'The legacy snapshot-aggregate endpoint is no longer supported.'
  );
}

/**
 * Snapshot V2 Result from snapshot-run-v2 edge function
 * PLA-138: Explicit swing selection with provenance
 */
export interface SnapshotV2Result {
  snapshot_v2_id: string;
  snapshot_v2_run_id: string;
  swingRunIds: string[];
  primary_fault_id: string | null;
  ready_for_training_plan: boolean;
  durationMs?: number;
}

/**
 * Create a Snapshot V2 from exactly 5 swing IDs
 * PLA-138: Uses snapshot-run-v2 with explicit swing selection
 */
export async function createSnapshotV2(swingIds: string[]): Promise<SnapshotV2Result> {
  if (swingIds.length !== 5) {
    throw new Error('Must provide exactly 5 swing IDs');
  }
  return edgeInvoke<SnapshotV2Result>('snapshot-run-v2', { swing_ids: swingIds });
}

/**
 * Get a snapshot by ID
 */
export async function getSnapshot(snapshotId: string): Promise<SnapshotResponse> {
  return edgeGet<SnapshotResponse>(`snapshot-get/${snapshotId}`);
}

/**
 * Get available swings for the current user
 */
export async function getAvailableSwings(): Promise<Array<{
  id: string;
  created_at: string;
  thumbnail_url: string | null;
  filename: string | null;
  has_analysis: boolean;
}>> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('Not authenticated');
  }

  // Prefer unified view so snapshot selection matches Library ordering/sources
  const { data: unifiedRuns, error: unifiedError } = await supabase
    .from('swing_runs_unified')
    .select(
      'run_id, created_at, updated_at, cf_playback_url, cf_stream_uid, mux_playback_id, playback_policy, status, original_filename, title, analysis_status'
    )
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false })
    .limit(50);

  if (!unifiedError && unifiedRuns?.length) {
    const runIds = unifiedRuns
      .map((run) => run.run_id)
      .filter((runId): runId is string => !!runId);

    let analysisLookup = new Map<string, boolean>();
    if (runIds.length > 0) {
      const { data: analysisRuns } = await supabase
        .from('swing_runs')
        .select('id, analysis_data_v3, analysis_enriched, ai_result')
        .in('id', runIds);

      if (analysisRuns) {
        analysisLookup = new Map(
          analysisRuns.map((run) => [
            run.id,
            !!(run.analysis_data_v3 || run.analysis_enriched || run.ai_result),
          ])
        );
      }
    }

    const available = unifiedRuns
      .filter((run) => !!run.run_id)
      .map((run) => {
        const cfStreamUid = run.cf_stream_uid || getCloudflareUidFromPlaybackUrl(run.cf_playback_url);
        const hasAnalysisFromRuns = run.run_id ? analysisLookup.get(run.run_id) : false;
        const hasAnalysis = run.analysis_status === 'completed' || !!hasAnalysisFromRuns;
        const thumbnail_url = getThumbnailUrl({
          id: run.run_id!,
          cf_stream_uid: cfStreamUid,
          cf_playback_url: run.cf_playback_url,
          mux_playback_id: run.mux_playback_id,
          playback_policy: run.playback_policy,
          status: run.status ?? undefined,
        });

        return {
          id: run.run_id!,
          created_at: run.created_at || run.updated_at || '',
          thumbnail_url,
          filename: run.original_filename || run.title || null,
          has_analysis: hasAnalysis,
        };
      });

    const analyzed = available.filter((run) => run.has_analysis);
    if (analyzed.length > 0) {
      return analyzed;
    }
  }

  // Fallback: swing_runs + swing_analysis_runs for legacy data
  const { data: swingRuns } = await supabase
    .from('swing_runs')
    .select('id, created_at, cf_playback_url, original_filename, analysis_enriched, ai_result')
    .eq('user_id', session.user.id)
    .eq('status', 'ready')
    .order('created_at', { ascending: false })
    .limit(50);

  const { data: analysisRuns } = await supabase
    .from('swing_analysis_runs')
    .select('id, created_at, ai_mediapipe_v1_enriched, ai_mediapipe_v1')
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false })
    .limit(50);

  const available: Array<{
    id: string;
    created_at: string;
    thumbnail_url: string | null;
    filename: string | null;
    has_analysis: boolean;
  }> = [];

  // Process swing_runs
  if (swingRuns) {
    for (const run of swingRuns) {
      const hasAnalysis = !!(run.analysis_enriched || run.ai_result);
      available.push({
        id: run.id,
        created_at: run.created_at || '',
        thumbnail_url: run.cf_playback_url,
        filename: run.original_filename,
        has_analysis: hasAnalysis
      });
    }
  }

  // Process swing_analysis_runs (avoid duplicates)
  if (analysisRuns) {
    const existingIds = new Set(available.map(s => s.id));
    for (const run of analysisRuns) {
      if (!existingIds.has(run.id)) {
        const hasAnalysis = !!(run.ai_mediapipe_v1_enriched || run.ai_mediapipe_v1);
        available.push({
          id: run.id,
          created_at: run.created_at,
          thumbnail_url: null,
          filename: null,
          has_analysis: hasAnalysis
        });
      }
    }
  }

  // Sort by created_at descending
  available.sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });

  // Filter to only those with analysis
  return available.filter(s => s.has_analysis);
}

/**
 * Snapshot V2 Response from snapshot-get-v2 edge function
 * PLA-138: V2 data for Snapshot Results UI
 */
export interface SnapshotV2Response {
  snapshot_v2: any;
  run_id: string;
  primary_fault: string | null;
  consistent_patterns: Array<{
    flaw_id: string;
    frequency: number;
    severity_range: { min: number; max: number; average: number };
    consistency_score: number;
  }>;
  root_cause_chain: Array<{
    flaw_id: string;
    position_in_chain: number;
    causal_relationship: 'root_cause' | 'enabler' | 'compensator' | 'symptom';
    evidence: string;
  }>;
  snapshot_mode: 'pattern_analysis' | 'consistency_profile';
  swing_run_ids: string[];
  ready_for_training_plan: boolean;
  created_at?: string;
}

/**
 * Get Snapshot V2 data by run ID
 * PLA-138: Fetches V2 data for Snapshot Results UI
 */
export async function getSnapshotV2(runId: string): Promise<SnapshotV2Response | null> {
  try {
    return await edgeGet<SnapshotV2Response>(`snapshot-get-v2/${runId}`);
  } catch (error) {
    console.warn('[getSnapshotV2] Snapshot V2 not available:', error);
    return null; // V2 not available, UI should show fallback
  }
}

/**
 * Get latest snapshot for the current user
 */
export async function getLatestSnapshot(): Promise<SnapshotResponse | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('Not authenticated');
  }

  const { data: snapshot, error } = await supabase
    .from('five_swing_snapshots')
    .select('id')
    .eq('user_id', session.user.id)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error || !snapshot) {
    return null;
  }

  return getSnapshot(snapshot.id);
}

